import React, { PropsWithChildren, useMemo } from 'react';
import Select, { GroupBase, MultiValue, MultiValueProps } from 'react-select';

import { useTranslations } from '../../../contexts/LocalizationContext';
import { RestaurantResponse } from '../../../modules/Restaurants/models';
import { cutLongString } from '../../utils/helpers';
import { SelectOption } from '../Select/models/select-option';
import { selectStyle } from '../Select/select-styles';

const INPUT_TRIMMED_VALUE_LIMIT = 23;

const MultiValues = ({
  children,
  ...props
}: PropsWithChildren<
  MultiValueProps<{ value: string; label: string }, true, GroupBase<{ value: string; label: string }>>
>) => {
  const translations = useTranslations();
  const { getValue, index, options } = props;
  if (index > 0) return null;

  const { length } = getValue();
  let inputValue;
  if (length === 1) {
    inputValue = cutLongString(getValue()[0].label, INPUT_TRIMMED_VALUE_LIMIT);
  }
  if (options.length === length && length !== 1) {
    inputValue = translations('all_locations', {
      '{{count}}': length.toString(),
    });
  }
  if (length > 1 && options.length !== length) {
    inputValue = translations('selected_locations', {
      '{{count}}': length.toString(),
    });
  }
  return <div>{inputValue}</div>;
};

type RestaurantMultiSelectProps = {
  selectedRestaurantIds: string[];
  restaurants: RestaurantResponse[];
  onSelectRestaurants: (value: MultiValue<SelectOption>) => void;
};

const RestaurantMultiSelect = ({
  selectedRestaurantIds,
  restaurants,
  onSelectRestaurants,
}: RestaurantMultiSelectProps) => {
  const restaurantSelectOptions = useMemo(
    () =>
      restaurants.map((restaurant) => ({
        value: restaurant.id,
        label: restaurant.restaurantName,
      })),
    [restaurants],
  );

  return (
    <Select
      value={restaurantSelectOptions.filter((option) => selectedRestaurantIds.includes(option.value))}
      isMulti
      onChange={onSelectRestaurants}
      options={restaurantSelectOptions}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      components={{ MultiValue: MultiValues }}
      styles={selectStyle<SelectOption, true>()}
      menuPlacement="auto"
    />
  );
};

export default RestaurantMultiSelect;
